import { Reveal } from "@/components/Reveal";

const Card = ({ label, figure, title, context, tid, delay = 0, bare = false, longFigure = false }) => (
  <Reveal delay={delay} className={bare ? "p-6 sm:p-8" : "border border-white/10 p-8 lg:p-10"}>
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-pinelight/70">{label}</p>
    <p
      data-testid={tid}
      className={
        longFigure
          ? "mt-6 font-mono font-medium text-xl sm:text-2xl text-[#3FA98C]"
          : "mt-6 font-headline font-semibold tracking-tight text-4xl sm:text-5xl text-[#3FA98C]"
      }
    >
      {figure}
    </p>
    <h3 className="mt-3 font-headline font-semibold text-lg text-white tracking-tight">{title}</h3>
    <p className="mt-3 text-sm text-white/60 leading-relaxed">{context}</p>
    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
      Full protocol and dataset under NDA.
    </p>
  </Reveal>
);

export const StudySummary = () => (
  <section className="bg-obsidian text-white py-20 lg:py-24 border-t border-white/10" data-testid="study-summary">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-pinelight/70">
          Study summary · Top-line results
        </p>
        <h2 className="mt-5 font-headline font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          The data behind the molecule.
        </h2>
        <p className="mt-5 text-white/60 text-base leading-relaxed max-w-3xl">
          Three completed studies, including a two-part canine trial. Top-line results below; full
          protocols, datasets and reports available to qualified partners under NDA.
        </p>
      </Reveal>

      <div className="mt-12 grid lg:grid-cols-2 gap-5">
        <Card
          tid="study-card-1"
          label="In vitro"
          figure="98%"
          title="Theobromine binding"
          context="Up to 98% of theobromine bound within 15 seconds in simulated canine gastric fluid."
          delay={0.05}
        />
        <Card
          tid="study-card-2"
          label="Murine model"
          figure="96% lower"
          title="Theobromine reduction in vivo"
          context="Murine model showed a 96% reduction in theobromine compared with control."
          delay={0.1}
        />

        <div className="lg:col-span-2 border border-pine/40 p-5 sm:p-7" data-testid="canine-trial-pair">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3FA98C] flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#3FA98C]/60" aria-hidden="true" />
            Canine trial
          </p>
          <div className="mt-2 grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
            <Card
              tid="study-card-3"
              bare
              label="Canine trial · Part 1, Safety"
              figure=">10×"
              title="Safety margin"
              context="No adverse effects observed at doses exceeding ten times the intended use level. Full veterinary blood chemistry panels collected."
              delay={0.15}
            />
            <Card
              tid="study-card-4"
              bare
              longFigure
              label="Canine trial · Part 2, Efficacy"
              figure="[Result to be supplied]"
              title="Theobromine reduction in dogs"
              context="Dogs received theobromine at sub-clinical doses under veterinary supervision. [Result statement to be supplied.]"
              delay={0.2}
            />
          </div>
        </div>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-12 border-l-2 border-pine bg-pine/10 p-8 lg:p-10" data-testid="study-callout">
          <h3 className="font-headline font-semibold tracking-tight text-xl sm:text-2xl text-white">
            The veterinary data, without a single dog harmed.
          </h3>
          <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed max-w-4xl">
            Our canine trial used sub-clinical theobromine doses under veterinary supervision and
            non-terminal study designs. No animal experienced clinical signs of toxicosis, and no
            animal was harmed. We produced the blood chemistry evidence veterinarians require
            without ever putting a dog at risk, and that is the standard we intend to hold as this
            product scales.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <p className="mt-8 text-xs text-white/40 leading-relaxed max-w-4xl" data-testid="study-footer-note">
          Results are from laboratory and animal studies conducted to support ingredient development.
          In vitro and animal data; not clinical claims. All canine work was conducted under veterinary
          supervision at sub-clinical exposure levels. Products containing this ingredient are subject
          to regulatory approval in each market.
        </p>
      </Reveal>
    </div>
  </section>
);
